/**
 * The account list, loaded once and shared.
 *
 * Nearly every screen needs it — the library shows only the kinds an account
 * ingested, the browse screens need an account id for every request — and it
 * changes rarely, so one fetch with an explicit `reload()` beats a fetch per
 * screen.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AccountSummary } from '@sflx/shared';
import { accounts as accountsApi, ApiRequestError } from './api';

const ACTIVE_KEY = 'sflx.activeAccount';

interface AccountsValue {
  accounts: AccountSummary[];
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
  active: AccountSummary | null;
  setActive: (id: string) => void;
}

const AccountsContext = createContext<AccountsValue | null>(null);

export function AccountsProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<AccountSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [activeId, setActiveId] = useState<string | null>(() => readActive());

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const result = await accountsApi.list();
      setList(result.accounts);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught : (caught as Error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const setActive = useCallback((id: string) => {
    setActiveId(id);
    try {
      window.localStorage.setItem(ACTIVE_KEY, id);
    } catch {
      // Private-mode storage failures are not worth surfacing; the selection
      // still works for this session.
    }
  }, []);

  // A remembered account that has since been deleted must not strand the UI on
  // an id that no longer resolves.
  const active = useMemo(
    () => list.find((account) => account.id === activeId) ?? list[0] ?? null,
    [list, activeId],
  );

  const value = useMemo<AccountsValue>(
    () => ({ accounts: list, loading, error, reload, active, setActive }),
    [list, loading, error, reload, active, setActive],
  );

  return <AccountsContext.Provider value={value}>{children}</AccountsContext.Provider>;
}

export function useAccounts(): AccountsValue {
  const value = useContext(AccountsContext);
  if (!value) throw new Error('useAccounts must be used inside <AccountsProvider>');
  return value;
}

function readActive(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}
